import { AbstractControl } from '@angular/forms';
import { Expose, Transform } from 'class-transformer';

export class WhiteLabelSettingsModel {
  @Expose({ name: 'id' })
  id!: number;
  @Expose({ name: 'has_own' })
  hasOwn!: boolean;
  @Expose({ name: 'white_labelable_type' })
  whiteLabelableType!: WhiteLabelSettingsScope;
  @Expose({ name: 'white_labelable_id' })
  whiteLabelableId!: number;
  @Expose({ name: 'status' })
  status!: WhiteLabelSettingsStatus;
  @Expose({ name: 'subdomain_name' })
  subdomainName!: string;
  // General colors
  @Expose({ name: 'header_color' })
  headerColor!: string;
  @Expose({ name: 'background_color' })
  backgroundColor!: string;
  @Expose({ name: 'border_color' })
  borderColor!: string;
  @Expose({ name: 'border_hover_color' })
  borderHoverColor!: string;
  @Expose({ name: 'sidebar_tab_selected_state' })
  sidebarTabSelectedState!: string;
  @Expose({ name: 'sidebar_tab_hovered_state' })
  sidebarTabHoveredState!: string;
  @Expose({ name: 'accent_color' })
  accentColor!: string;
  @Expose({ name: 'options_header_color' })
  optionsHeaderColor!: string;
  @Expose({ name: 'checkbox_color' })
  checkboxColor!: string;
  @Expose({ name: 'checkbox_hover_color' })
  checkboxHoverColor!: string;
  @Expose({ name: 'tab_selected_state' })
  tabSelectedState!: string;
  @Expose({ name: 'tab_hovered_state' })
  tabHoveredState!: string;
  // Text
  @Expose({ name: 'primary_text_color' })
  primaryTextColor!: string;
  @Expose({ name: 'secondary_text_color' })
  secondaryTextColor!: string;
  @Expose({ name: 'placeholder_text_color' })
  placeholderTextColor!: string;
  @Expose({ name: 'disabled_text_color' })
  disabledTextColor!: string;

  // Primary button
  @Expose({ name: 'primary_button_background_rest' })
  primaryButtonBackgroundRest!: string;
  @Expose({ name: 'primary_button_background_hover' })
  primaryButtonBackgroundHover!: string;
  @Expose({ name: 'primary_button_background_disabled' })
  primaryButtonBackgroundDisabled!: string;
  @Expose({ name: 'primary_button_border_rest' })
  primaryButtonBorderRest!: string;
  @Expose({ name: 'primary_button_border_hover' })
  primaryButtonBorderHover!: string;
  @Expose({ name: 'primary_button_border_disabled' })
  primaryButtonBorderDisabled!: string;
  @Expose({ name: 'primary_button_text_color_rest' })
  primaryButtonTextColorRest!: string;
  @Expose({ name: 'primary_button_text_color_hover' })
  primaryButtonTextColorHover!: string;
  @Expose({ name: 'primary_button_text_color_disabled' })
  primaryButtonTextColorDisabled!: string;
  @Expose({ name: 'button_corner_radius' })
  buttonCornerRadius!: number;
  // Secondary button
  @Expose({ name: 'secondary_button_background_rest' })
  secondaryButtonBackgroundRest!: string;
  @Expose({ name: 'secondary_button_background_disabled' })
  secondaryButtonBackgroundDisabled!: string;
  @Expose({ name: 'secondary_button_background_hover' })
  secondaryButtonBackgroundHover!: string;
  @Expose({ name: 'secondary_button_text_color_rest' })
  secondaryButtonTextColorRest!: string;
  @Expose({ name: 'secondary_button_text_color_hover' })
  secondaryButtonTextColorHover!: string;
  @Expose({ name: 'secondary_button_text_color_disabled' })
  secondaryButtonTextColorDisabled!: string;
  @Expose({ name: 'secondary_button_border_rest' })
  secondaryButtonBorderRest!: string;
  @Expose({ name: 'secondary_button_border_hover' })
  secondaryButtonBorderHover!: string;
  @Expose({ name: 'secondary_button_border_disabled' })
  secondaryButtonBorderDisabled!: string;
  @Expose({ name: 'secondary_button_corner_radius' })
  secondaryButtonCornerRadius!: number;
  // Assets
  @Expose({ name: 'logo_url' })
  logoUrl!: string | null;
  @Expose({ name: 'logo' })
  @Transform(({ value }) => value?.id || null, { toClassOnly: true })
  logoFileId: number | null = null;
}

export enum WhiteLabelSettingsStatus {
  Active = 'active',
  Inactive = 'inactive',
}

export enum WhiteLabelSettingsScope {
  Project = 'Project',
  Firm = 'Firm',
}

export enum WhiteLabelSettingsFormKeys {
  // General colors
  HeaderColor = 'header_color',
  BackgroundColor = 'background_color',
  BorderColor = 'border_color',
  BorderHoverColor = 'border_hover_color',
  SidebarTabSelectedState = 'sidebar_tab_selected_state',
  SidebarTabHoveredState = 'sidebar_tab_hovered_state',
  AccentColor = 'accent_color',
  OptionsHeaderColor = 'options_header_color',
  CheckboxColor = 'checkbox_color',
  CheckboxHoverColor = 'checkbox_hover_color',
  TabSelectedState = 'tab_selected_state',
  TabHoveredState = 'tab_hovered_state',
  // Text
  PrimaryTextColor = 'primary_text_color',
  SecondaryTextColor = 'secondary_text_color',
  PlaceholderTextColor = 'placeholder_text_color',
  DisabledTextColor = 'disabled_text_color',
  // Primary button
  PrimaryButtonBackgroundRest = 'primary_button_background_rest',
  PrimaryButtonBackgroundHover = 'primary_button_background_hover',
  PrimaryButtonBackgroundDisabled = 'primary_button_background_disabled',
  PrimaryButtonBorderRest = 'primary_button_border_rest',
  PrimaryButtonBorderHover = 'primary_button_border_hover',
  PrimaryButtonBorderDisabled = 'primary_button_border_disabled',
  PrimaryButtonTextColorRest = 'primary_button_text_color_rest',
  PrimaryButtonTextColorHover = 'primary_button_text_color_hover',
  PrimaryButtonTextColorDisabled = 'primary_button_text_color_disabled',
  ButtonCornerRadius = 'button_corner_radius',
  // Secondary button
  SecondaryButtonBackgroundRest = 'secondary_button_background_rest',
  SecondaryButtonBackgroundDisabled = 'secondary_button_background_disabled',
  SecondaryButtonBackgroundHover = 'secondary_button_background_hover',
  SecondaryButtonTextColorRest = 'secondary_button_text_color_rest',
  SecondaryButtonTextColorHover = 'secondary_button_text_color_hover',
  SecondaryButtonTextColorDisabled = 'secondary_button_text_color_disabled',
  SecondaryButtonBorderRest = 'secondary_button_border_rest',
  SecondaryButtonBorderHover = 'secondary_button_border_hover',
  SecondaryButtonBorderDisabled = 'secondary_button_border_disabled',
  SecondaryButtonCornerRadius = 'secondary_button_corner_radius',
  // Assets
  LogoUrl = 'logo_url',
  LogoFile = 'logo_file',
  LogoFileId = 'logo',
}

export type WhiteLabelSettingsFormControl = {
  [key in WhiteLabelSettingsFormKeys]: AbstractControl<any>;
};

export type WhiteLabelSettingsFormRawValue = {
  [key in WhiteLabelSettingsFormKeys]: any;
};

export interface WhiteLabelSettingsInputListItem {
  formControlName: WhiteLabelSettingsFormKeys;
  labelTranslateKey: string;
  isNumeric?: boolean;
  mask?: string;
}
